import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { useAuth } from '../../contexts/AuthContext';
import { Shield, Plus, Save, Users, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminRoles = () => {
  const { token } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('none');
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingUser, setSavingUser] = useState(false);
  const [templateDialog, setTemplateDialog] = useState(false);
  const [templateForm, setTemplateForm] = useState({ id: '', name: '', description: '', permissions: {} });

  useEffect(() => { if (token) fetchData(); }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [templatesRes, usersRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/role-templates`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const templatesData = await templatesRes.json();
      const usersData = await usersRes.json();
      setTemplates(templatesData.templates || []);
      setAvailablePermissions(templatesData.available_permissions || []);
      const employeeUsers = (usersData || []).filter((user) => user.role === 'employee');
      setUsers(employeeUsers);
      if (employeeUsers[0]) {
        selectUser(employeeUsers[0]);
      }
    } catch (error) {
      console.error(error);
      toast.error('Rollen-Daten konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  const selectUser = (user) => {
    setSelectedUserId(user.id);
    setSelectedTemplateId(user.role_template_id || 'none');
    setOverrides(user.permission_overrides || {});
  };

  const selectedUser = useMemo(() => users.find((item) => item.id === selectedUserId), [users, selectedUserId]);

  const saveUserPermissions = async () => {
    if (!selectedUserId) return;
    setSavingUser(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${selectedUserId}/permissions`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ role_template_id: selectedTemplateId === 'none' ? null : selectedTemplateId, permission_overrides: overrides }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Rechte konnten nicht gespeichert werden');
      toast.success('Mitarbeiterrechte gespeichert.');
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Fehler');
    } finally {
      setSavingUser(false);
    }
  };

  const openNewTemplate = () => {
    setTemplateForm({ id: '', name: '', description: '', permissions: availablePermissions.reduce((acc, perm) => ({ ...acc, [perm]: false }), {}) });
    setTemplateDialog(true);
  };

  const openEditTemplate = (template) => {
    setTemplateForm({ id: template.id, name: template.name, description: template.description || '', permissions: { ...template.permissions } });
    setTemplateDialog(true);
  };

  const saveTemplate = async () => {
    try {
      const method = templateForm.id ? 'PUT' : 'POST';
      const url = templateForm.id ? `${API_URL}/api/admin/role-templates/${templateForm.id}` : `${API_URL}/api/admin/role-templates`;
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: templateForm.name, description: templateForm.description, permissions: templateForm.permissions }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Vorlage konnte nicht gespeichert werden');
      toast.success(templateForm.id ? 'Vorlage aktualisiert.' : 'Vorlage erstellt.');
      setTemplateDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Fehler');
    }
  };

  const deleteTemplate = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/role-templates/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Vorlage konnte nicht gelöscht werden');
      toast.success('Vorlage gelöscht.');
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Fehler');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-roles-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold">Rollen & Mitarbeiterrechte</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Rollenvorlagen definieren und pro Mitarbeiter gezielt überschreiben</p>
        </div>
        <Button onClick={openNewTemplate} data-testid="role-template-new-button"><Plus className="w-4 h-4 mr-2" />Neue Vorlage</Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Shield className="w-5 h-5" /> Rollenvorlagen</CardTitle>
            <CardDescription>Wiederverwendbare Rechte-Sets für Mitarbeiter</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {loading ? <p className="text-sm text-[hsl(var(--muted-foreground))]">Lädt...</p> : templates.length === 0 ? <p className="text-sm text-[hsl(var(--muted-foreground))]">Noch keine Vorlagen.</p> : templates.map((template) => (
              <div key={template.id} className="rounded-xl border border-[hsl(var(--border))] p-4 space-y-3" data-testid={`role-template-${template.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-medium">{template.name}</p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">{template.description || 'Ohne Beschreibung'}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEditTemplate(template)} data-testid={`role-template-edit-${template.id}`}>Bearbeiten</Button>
                    <Button size="sm" variant="outline" className="text-red-500" onClick={() => deleteTemplate(template.id)} data-testid={`role-template-delete-${template.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {availablePermissions.filter((perm) => template.permissions?.[perm]).map((perm) => <Badge key={perm} variant="outline">{perm}</Badge>)}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5" /> Mitarbeiter-Zuweisung</CardTitle>
            <CardDescription>Vorlage wählen und individuelle Overrides speichern</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Mitarbeiter</Label>
              <Select value={selectedUserId} onValueChange={(value) => selectUser(users.find((item) => item.id === value))}>
                <SelectTrigger data-testid="roles-user-select"><SelectValue placeholder="Mitarbeiter wählen" /></SelectTrigger>
                <SelectContent>
                  {users.map((user) => <SelectItem key={user.id} value={user.id}>{user.name || user.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Vorlage</Label>
              <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                <SelectTrigger data-testid="roles-template-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Keine Vorlage / nur Overrides</SelectItem>
                  {templates.map((template) => <SelectItem key={template.id} value={template.id}>{template.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {selectedUser && (
              <div className="rounded-xl border border-[hsl(var(--border))] p-4 space-y-3" data-testid="roles-overrides-card">
                <p className="font-medium">Individuelle Rechte für {selectedUser.name || selectedUser.email}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {availablePermissions.map((perm) => (
                    <label key={perm} className="flex items-center gap-3 rounded-lg border border-[hsl(var(--border))] px-3 py-2 cursor-pointer">
                      <Checkbox checked={!!overrides[perm]} onCheckedChange={(checked) => setOverrides((prev) => ({ ...prev, [perm]: !!checked }))} data-testid={`roles-override-${perm}`} />
                      <span className="text-sm">{perm}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <Button onClick={saveUserPermissions} disabled={!selectedUserId || savingUser} data-testid="roles-save-user-permissions">
              <Save className="w-4 h-4 mr-2" /> Rechte speichern
            </Button>
          </CardContent>
        </Card>
      </div>

      <Dialog open={templateDialog} onOpenChange={setTemplateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{templateForm.id ? 'Rollenvorlage bearbeiten' : 'Neue Rollenvorlage'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={templateForm.name} onChange={(e) => setTemplateForm((prev) => ({ ...prev, name: e.target.value }))} data-testid="role-template-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Beschreibung</Label>
              <Textarea value={templateForm.description} onChange={(e) => setTemplateForm((prev) => ({ ...prev, description: e.target.value }))} rows={3} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {availablePermissions.map((perm) => (
                <label key={perm} className="flex items-center gap-3 rounded-lg border border-[hsl(var(--border))] px-3 py-2 cursor-pointer">
                  <Checkbox checked={!!templateForm.permissions[perm]} onCheckedChange={(checked) => setTemplateForm((prev) => ({ ...prev, permissions: { ...prev.permissions, [perm]: !!checked } }))} data-testid={`role-template-permission-${perm}`} />
                  <span className="text-sm">{perm}</span>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialog(false)}>Abbrechen</Button>
            <Button onClick={saveTemplate} data-testid="role-template-save-button">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRoles;